// src/composables/usePushNotifications.js
import { ref, computed } from 'vue'
import { officeService } from '@/services/api'

/**
 * Convert a base64url VAPID public key to a Uint8Array,
 * which is what PushManager.subscribe() requires.
 */
function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Platform detection for the iOS PWA caveat
const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true

export function usePushNotifications() {
  const permission = ref(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const subscribed = ref(false)
  const busy = ref(false)
  const error = ref(null)

  const isSupported = computed(() => {
    if (typeof window === 'undefined') return false
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  })

  const iosNeedsInstall = computed(() => isIos() && !isStandalone())

  async function refreshSubscriptionState() {
    if (!isSupported.value) return
    permission.value = Notification.permission
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) {
        subscribed.value = false
        return
      }
      const sub = await reg.pushManager.getSubscription()
      if (!sub || Notification.permission !== 'granted') {
        subscribed.value = false
        return
      }

      // Browser permission/subscription is device-wide. The bell reflects
      // whether this endpoint is registered for the currently signed-in user.
      const { data } = await officeService.getPushSubscriptionStatus(sub)
      subscribed.value = data?.success === true && data?.subscribed === true
    } catch (e) {
      console.warn('[push] refresh state failed:', e)
      subscribed.value = false
    }
  }

  async function ensureServiceWorker() {
    // Reuse existing registration if present, otherwise register
    let reg = await navigator.serviceWorker.getRegistration()
    if (!reg) {
      reg = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      })
    }
    await navigator.serviceWorker.ready
    return reg
  }

  async function getVapidKey() {
    // Prefer server-provided key so rotation works without a rebuild
    try {
      const { data } = await officeService.getVapidPublicKey()
      if (data?.public_key) return data.public_key
    } catch (e) {
      console.warn('[push] failed to fetch VAPID key from server:', e)
    }
    // Fallback to env (must be set at build time)
    const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY
    if (envKey) return envKey
    throw new Error('VAPID public key not available')
  }

async function subscribe() {
  console.log('[push] subscribe() start')

  if (!isSupported.value) throw new Error('not supported')
  if (iosNeedsInstall.value) throw new Error('ios needs install')

  busy.value = true
  error.value = null
  try {
    console.log('[push] step 1: requesting permission')
    const perm = await Notification.requestPermission()
    console.log('[push] permission:', perm)
    permission.value = perm
    if (perm !== 'granted') throw new Error('Notification permission not granted')

    console.log('[push] step 2: ensuring service worker')
    const reg = await ensureServiceWorker()
    console.log('[push] SW ready. scope:', reg.scope)

    console.log('[push] step 3: checking existing subscription')
    let sub = await reg.pushManager.getSubscription()
    console.log('[push] existing sub:', sub)

    if (!sub) {
      console.log('[push] step 4: fetching VAPID key')
      const vapidKey = await getVapidKey()
      console.log('[push] vapid key (first 20 chars):', vapidKey?.slice(0, 20))
      console.log('[push] vapid key length:', vapidKey?.length)

      const applicationServerKey = urlB64ToUint8Array(vapidKey)
      console.log('[push] applicationServerKey byte length:', applicationServerKey.length)

      console.log('[push] calling pushManager.subscribe()')
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })
      console.log('[push] subscribe() resolved:', sub)
    }

    console.log('[push] step 5: sending to backend')
    await officeService.subscribePush(sub)
    console.log('[push] backend acknowledged')

    subscribed.value = true
    return sub
  } catch (e) {
    console.error('[push] FAILED:', e)
    error.value = e?.message || String(e)
    throw e
  } finally {
    busy.value = false
  }
}

  async function unsubscribe() {
    busy.value = true
    error.value = null
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (reg) {
        const sub = await reg.pushManager.getSubscription()
        if (sub) {
          // Disable notifications for this account/device pair only. Keep the
          // browser endpoint intact for other accounts or a later re-enable.
          await officeService.unsubscribePush(sub)
        }
      }
      subscribed.value = false
    } catch (e) {
      error.value = e?.message || String(e)
      throw e
    } finally {
      busy.value = false
    }
  }

  return {
    permission,
    subscribed,
    busy,
    error,
    isSupported,
    iosNeedsInstall,
    subscribe,
    unsubscribe,
    refreshSubscriptionState,
  }
}
